import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Clock, Wrench } from 'lucide-react';
import { format } from 'date-fns';
import AvailabilityGraph from '../components/AvailabilityGraph';
import { config } from '../config';

interface Component {
  id: string;
  display_name: string;
  status: string;
  region?: string;
  environment?: string;
  has_active_incident?: boolean;
  incident_severity?: string;
}

interface Incident {
  id: string;
  title: string;
  severity: string;
  state: string;
  started_at: string;
  components?: Array<{ id: string; display_name: string }>;
}

interface SystemStatus {
  status: string;
  updated_at: string;
}

export default function StatusPage() {
  const [components, setComponents] = useState<Component[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const apiUrl = config.apiUrl;

      const [statusRes, componentsRes, incidentsRes] = await Promise.all([
        fetch(`${apiUrl}/v1/stats/status`),
        fetch(`${apiUrl}/v1/stats/components`),
        fetch(`${apiUrl}/v1/stats/incidents`)
      ]);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }

      if (componentsRes.ok) {
        const componentsData = await componentsRes.json();
        setComponents(componentsData);
      }

      if (incidentsRes.ok) {
        const incidentsData = await incidentsRes.json();
        setIncidents(incidentsData);
      }
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'OPERATIONAL':
        return <CheckCircle className="h-5 w-5" />;
      case 'DEGRADED_PERFORMANCE':
      case 'DEGRADED':
      case 'PARTIAL_OUTAGE':
      case 'MAJOR_OUTAGE':
      case 'OUTAGE':
        return <AlertCircle className="h-5 w-5" />;
      case 'MAINTENANCE':
        return <Wrench className="h-5 w-5" />;
      default:
        return <Clock className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-10 h-10 border-2 border-gray-200 rounded-full" />
          <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-unlimit-lime rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const isOperational = status?.status === 'OPERATIONAL';

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Hero Status Banner */}
      <div className={`rounded-2xl p-8 ${
        isOperational
          ? 'bg-navy-900'
          : 'bg-gradient-to-br from-rose-400 to-rose-500'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">System Status</h1>
            <p className="text-gray-400 mt-1 text-sm">
              Current status of all Unlimit services
            </p>
          </div>
          <div className={`flex items-center space-x-2.5 px-5 py-2.5 rounded-xl font-semibold text-sm ${
            isOperational
              ? 'bg-unlimit-lime text-navy-900'
              : 'bg-white/15 text-white'
          }`}>
            {getStatusIcon(status?.status || 'OPERATIONAL')}
            <span>
              {isOperational ? 'All Systems Operational' : status?.status || 'OPERATIONAL'}
            </span>
          </div>
        </div>
      </div>

      {/* Active Incidents */}
      {incidents.length > 0 && (
        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 animate-fade-in">
          <h2 className="text-base font-bold text-rose-400 mb-4 flex items-center space-x-2">
            <AlertCircle className="h-4 w-4" />
            <span>Active Incidents</span>
          </h2>
          <div className="space-y-3">
            {incidents.map((incident) => (
              <Link
                key={incident.id}
                to={`/incidents/${incident.id}`}
                className="block bg-white rounded-xl p-5 border border-rose-100 hover:shadow-card-hover transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-navy-900">{incident.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Started {format(new Date(incident.started_at), 'PPp')}
                    </p>
                    {incident.components && incident.components.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {incident.components.map((component) => (
                          <span
                            key={component.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600"
                          >
                            {component.display_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-md ${
                    incident.severity === 'CRITICAL' ? 'bg-rose-50 text-rose-400' :
                    incident.severity === 'MAJOR' ? 'bg-amber-50 text-amber-500' :
                    'bg-yellow-50 text-yellow-500'
                  }`}>
                    {incident.severity}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <AvailabilityGraph days={90} />

      {/* Service Components */}
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-base font-bold text-navy-900">Service Components</h2>
        </div>
        <div className="divide-y divide-gray-50">
          {components.map((component) => (
            <div key={component.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors">
              <div>
                <h3 className="font-semibold text-navy-900 text-sm">{component.display_name}</h3>
                {(component.region || component.environment) && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {component.region && <span className="capitalize">{component.region}</span>}
                    {component.region && component.environment && ' \u00b7 '}
                    {component.environment && <span className="capitalize">{component.environment}</span>}
                  </p>
                )}
              </div>
              <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
                component.has_active_incident
                  ? component.incident_severity === 'CRITICAL'
                    ? 'bg-rose-50 text-rose-400'
                    : component.incident_severity === 'MAJOR'
                    ? 'bg-amber-50 text-amber-500'
                    : 'bg-yellow-50 text-yellow-500'
                  : component.status === 'OPERATIONAL'
                  ? 'bg-emerald-50 text-emerald-700'
                  : component.status === 'DEGRADED_PERFORMANCE' || component.status === 'DEGRADED'
                  ? 'bg-yellow-50 text-yellow-500'
                  : component.status === 'PARTIAL_OUTAGE' || component.status === 'MAJOR_OUTAGE' || component.status === 'OUTAGE'
                  ? 'bg-rose-50 text-rose-400'
                  : 'bg-blue-50 text-blue-700'
              }`}>
                {component.has_active_incident ? (
                  <>
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span>
                      {component.incident_severity === 'CRITICAL' ? 'Critical' :
                       component.incident_severity === 'MAJOR' ? 'Major' : 'Minor'} Incident
                    </span>
                  </>
                ) : component.status === 'OPERATIONAL' ? (
                  <>
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span>Operational</span>
                  </>
                ) : (
                  <span className="capitalize">{component.status.replace(/_/g, ' ')}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
